import { API_BASE_URL } from './apiBase';
import { isCurrentHashRoute, redirectToHashRoute } from './hashRoutes';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  isFirstLogin?: boolean;
}

interface AuthResponse {
  token: string;
  user: User;
  expires_in_seconds?: number;
  idle_timeout_seconds?: number;
}

interface LoginErrorResponse {
  error?: string;
  code?: string;
  requires_challenge?: boolean;
  requires_verification?: boolean;
  attempts_remaining?: number;
  retry_after_seconds?: number;
  account_locked?: boolean;
  lockout_until?: number;
  requires_password_reset?: boolean;
  warning_next_failure_requires_password_reset?: boolean;
  registration_blocked?: boolean;
  permanent?: boolean;
  requires_login?: boolean;
  registration_reopens_at?: number;
  registration_reset_time_local?: string;
  registration_reset_timezone?: string;
  verification_id?: string;
  destination?: string;
  expires_at?: number;
  expires_in_seconds?: number;
  message?: string;
}

interface VerificationPendingResponse {
  success?: boolean;
  requires_verification?: boolean;
  verification_id?: string;
  destination?: string;
  expires_at?: number;
  expires_in_seconds?: number;
  message?: string;
}

interface MeResponse {
  user: User;
}

let currentUser: User | null = null;
let currentToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let activityMonitorStarted = false;
let lastActivityAt = Date.now();
let idleTimeoutMs = 15 * 60 * 1000;
let cachedDeviceFingerprintPromise: Promise<string> | null = null;
const DASHBOARD_SESSION_HINT_KEY = 'dashboard_session_hint_v1';
const AUTH_BOOTSTRAP_ATTEMPTS = 3;
const AUTH_BOOTSTRAP_TIMEOUT_MS = 5000;
const AUTH_BOOTSTRAP_RETRY_DELAY_MS = 900;

function setSessionHint(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (enabled) {
      window.sessionStorage.setItem(DASHBOARD_SESSION_HINT_KEY, '1');
    } else {
      window.sessionStorage.removeItem(DASHBOARD_SESSION_HINT_KEY);
    }
  } catch {
    // Best-effort only; auth still works without storage access.
  }
}

export function hasSessionHint(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(DASHBOARD_SESSION_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isUnauthorizedStatus(status: number): boolean {
  return status === 401 || status === 403;
}

async function sha256Hex(value: string): Promise<string> {
  const input = new TextEncoder().encode(String(value || ''));
  const digest = await crypto.subtle.digest('SHA-256', input);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function resolveDeviceFingerprint(): Promise<string> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'server-fallback-fingerprint';
  }

  const nav = navigator as Navigator & {
    deviceMemory?: number;
    userAgentData?: { platform?: string; mobile?: boolean };
  };
  const screenInfo = typeof screen !== 'undefined'
    ? `${screen.width}x${screen.height}x${screen.colorDepth}`
    : 'no-screen';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown-timezone';
  const components = [
    nav.userAgent || 'unknown-ua',
    nav.language || 'unknown-language',
    String(nav.languages || ''),
    String(nav.platform || nav.userAgentData?.platform || ''),
    String(nav.hardwareConcurrency || ''),
    String(nav.deviceMemory || ''),
    String(nav.userAgentData?.mobile || false),
    screenInfo,
    timezone,
  ];
  return sha256Hex(components.join('|'));
}

export async function getDeviceFingerprint(): Promise<string> {
  if (!cachedDeviceFingerprintPromise) {
    cachedDeviceFingerprintPromise = resolveDeviceFingerprint().catch(() => 'fingerprint-unavailable');
  }
  return cachedDeviceFingerprintPromise;
}

function applyAuthResponse(data: AuthResponse): void {
  currentToken = data.token;
  currentUser = data.user;
  setSessionHint(true);
  if (Number.isFinite(data.idle_timeout_seconds)) {
    idleTimeoutMs = Math.max(60_000, Number(data.idle_timeout_seconds) * 1000);
  }
}

function clearAuthState(options: { clearSessionHint?: boolean } = {}): void {
  currentUser = null;
  currentToken = null;
  if (options.clearSessionHint) {
    setSessionHint(false);
  }
}

function setCurrentUser(user: User): void {
  currentUser = {
    ...user,
    isFirstLogin:
      typeof user.isFirstLogin === 'boolean'
        ? user.isFirstLogin
        : currentUser?.id === user.id
          ? Boolean(currentUser?.isFirstLogin)
          : false,
  };
}

function redirectToLogin(reason: string): void {
  if (typeof window === 'undefined') return;
  if (isCurrentHashRoute('/login')) return;
  redirectToHashRoute('/login', { reason, expired: '1' });
}

function markActivity(): void {
  lastActivityAt = Date.now();
}

function startActivityMonitor(): void {
  if (activityMonitorStarted || typeof window === 'undefined') return;
  activityMonitorStarted = true;

  const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'focus'];
  for (const eventName of events) {
    window.addEventListener(eventName, markActivity, { passive: true });
  }

  window.setInterval(async () => {
    if (!currentUser || !currentToken) return;

    const now = Date.now();
    const idleForMs = now - lastActivityAt;
    if (idleForMs > idleTimeoutMs) {
      await logout({ redirectToLogin: true, reason: 'idle_timeout' });
      return;
    }

    // Keep session alive while actively interacting with the UI.
    const recentlyActive = idleForMs <= Math.min(120_000, idleTimeoutMs / 2);
    if (recentlyActive) {
      await refreshSession({ silent: true, redirectOnFailure: true });
    }
  }, 60_000);
}

export async function refreshSession(
  options: { silent?: boolean; redirectOnFailure?: boolean; timeoutMs?: number } = {},
): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const hadSession = Boolean(currentUser && currentToken);
    const controller = typeof AbortController !== 'undefined' && Number.isFinite(options.timeoutMs)
      ? new AbortController()
      : null;
    const timeoutMs = controller ? Math.max(1000, Number(options.timeoutMs)) : 0;
    const timeoutId = controller
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        signal: controller?.signal,
      });
      if (!response.ok) {
        if (isUnauthorizedStatus(response.status)) {
          clearAuthState({ clearSessionHint: true });
          if (options.redirectOnFailure && hadSession) {
            redirectToLogin('session_expired');
          }
        }
        return false;
      }
      const data = await response.json() as AuthResponse;
      applyAuthResponse(data);
      return true;
    } catch {
      return false;
    } finally {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function initializeAuth(): Promise<'authenticated' | 'unauthenticated' | 'retryable'> {
  startActivityMonitor();
  markActivity();
  const shouldRetry = hasSessionHint();
  const attempts = shouldRetry ? AUTH_BOOTSTRAP_ATTEMPTS : 1;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const refreshed = await refreshSession({ silent: true, timeoutMs: AUTH_BOOTSTRAP_TIMEOUT_MS });
    if (refreshed && isAuthenticated()) {
      return 'authenticated';
    }
    if (!hasSessionHint()) {
      return 'unauthenticated';
    }
    if (attempt < attempts - 1) {
      await delay(AUTH_BOOTSTRAP_RETRY_DELAY_MS);
    }
  }

  return shouldRetry ? 'retryable' : 'unauthenticated';
}

export async function register(
  username: string,
  email: string,
  firstName: string,
  password: string,
) {
  try {
    const payload: Record<string, unknown> = {
      username,
      email,
      firstName,
      password,
      device_fingerprint: await getDeviceFingerprint(),
    };

    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Registration failed' })) as LoginErrorResponse;
      return {
        success: false,
        error: String(error.error || 'Registration failed'),
        code: String(error.code || ''),
        requiresChallenge: Boolean(error.requires_challenge),
        retryAfterSeconds: Number.isFinite(error.retry_after_seconds) ? Number(error.retry_after_seconds) : undefined,
        registrationBlocked: Boolean(error.registration_blocked),
        permanent: Boolean(error.permanent),
        requiresLogin: Boolean(error.requires_login),
        registrationReopensAt: Number.isFinite(error.registration_reopens_at)
          ? Number(error.registration_reopens_at)
          : undefined,
        registrationResetTimeLocal: error.registration_reset_time_local
          ? String(error.registration_reset_time_local)
          : undefined,
        registrationResetTimezone: error.registration_reset_timezone
          ? String(error.registration_reset_timezone)
          : undefined,
        attemptsRemaining: Number.isFinite(error.attempts_remaining) ? Number(error.attempts_remaining) : undefined,
      };
    }

    const data = await response.json() as (AuthResponse & VerificationPendingResponse);
    if (data?.token && data?.user) {
      applyAuthResponse(data);
      startActivityMonitor();
      markActivity();
      return { success: true };
    }
    if (data?.requires_verification) {
      return {
        success: true,
        requiresVerification: true,
        verificationId: data.verification_id ? String(data.verification_id) : undefined,
        destination: data.destination ? String(data.destination) : undefined,
        expiresAt: Number.isFinite(data.expires_at) ? Number(data.expires_at) : undefined,
        expiresInSeconds: Number.isFinite(data.expires_in_seconds) ? Number(data.expires_in_seconds) : undefined,
        message: data.message ? String(data.message) : undefined,
      };
    }
    return { success: false, error: 'Unexpected registration response', code: 'invalid_response' };
  } catch (error: any) {
    return { success: false, error: error.message || 'Registration failed', code: 'network_error' };
  }
}

export async function verifyRegistrationCode(
  email: string,
  code: string,
  verificationId?: string | null
) {
  try {
    const payload: Record<string, unknown> = {
      email,
      code,
    };
    if (verificationId) {
      payload.verification_id = verificationId;
    }
    const response = await fetch(`${API_BASE_URL}/auth/register/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Verification failed' })) as LoginErrorResponse;
      return {
        success: false,
        error: String(error.error || 'Verification failed'),
        code: String(error.code || ''),
        attemptsRemaining: Number.isFinite(error.attempts_remaining) ? Number(error.attempts_remaining) : undefined,
      };
    }

    const data = await response.json() as AuthResponse;
    applyAuthResponse(data);
    startActivityMonitor();
    markActivity();
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Verification failed', code: 'network_error' };
  }
}

export async function login(username: string, password: string) {
  try {
    const payload: Record<string, unknown> = {
      username,
      password,
    };

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' })) as LoginErrorResponse;
      return {
        success: false,
        error: String(error.error || 'Login failed'),
        code: String(error.code || ''),
        requiresChallenge: Boolean(error.requires_challenge),
        attemptsRemaining: Number.isFinite(error.attempts_remaining) ? Number(error.attempts_remaining) : undefined,
        retryAfterSeconds: Number.isFinite(error.retry_after_seconds) ? Number(error.retry_after_seconds) : undefined,
        accountLocked: Boolean(error.account_locked),
        lockoutUntil: Number.isFinite(error.lockout_until) ? Number(error.lockout_until) : undefined,
        requiresPasswordReset: Boolean(error.requires_password_reset),
        warningNextFailureRequiresPasswordReset: Boolean(error.warning_next_failure_requires_password_reset),
      };
    }

    const data: AuthResponse = await response.json();
    applyAuthResponse(data);
    startActivityMonitor();
    markActivity();
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Login failed', code: 'network_error' };
  }
}

export async function requestPasswordResetCode(identifier: string) {
  try {
    const payload: Record<string, unknown> = { identifier };
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' })) as LoginErrorResponse;
      return {
        success: false,
        error: String(error.error || 'Request failed'),
        code: String(error.code || ''),
      };
    }

    const data = await response.json() as VerificationPendingResponse;
    return {
      success: true,
      message: String(data?.message || 'If an account exists, a verification code has been sent.'),
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Request failed', code: 'network_error' };
  }
}

export async function resetPasswordWithCode(
  identifier: string,
  code: string,
  newPassword: string,
  verificationId?: string | null
) {
  try {
    const payload: Record<string, unknown> = {
      identifier,
      code,
      newPassword,
    };
    if (verificationId) {
      payload.verification_id = verificationId;
    }
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Reset failed' })) as LoginErrorResponse;
      return {
        success: false,
        error: String(error.error || 'Reset failed'),
        code: String(error.code || ''),
        attemptsRemaining: Number.isFinite(error.attempts_remaining) ? Number(error.attempts_remaining) : undefined,
      };
    }

    const data = await response.json().catch(() => ({} as { message?: string })) as { message?: string };
    return {
      success: true,
      message: String(data?.message || 'Password reset successful.'),
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Reset failed', code: 'network_error' };
  }
}

export async function requestDeleteAccountCode() {
  const token = currentToken;
  if (!token) {
    return { success: false, error: 'Not authenticated', code: 'unauthorized' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/delete-account/request-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({}),
    });

    if (response.status === 401) {
      const refreshed = await refreshSession({ silent: true });
      if (refreshed && currentToken) {
        return requestDeleteAccountCode();
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' })) as LoginErrorResponse;
      return {
        success: false,
        error: String(error.error || 'Request failed'),
        code: String(error.code || ''),
      };
    }

    const data = await response.json() as VerificationPendingResponse;
    return {
      success: true,
      verificationId: data.verification_id ? String(data.verification_id) : undefined,
      destination: data.destination ? String(data.destination) : undefined,
      expiresAt: Number.isFinite(data.expires_at) ? Number(data.expires_at) : undefined,
      expiresInSeconds: Number.isFinite(data.expires_in_seconds) ? Number(data.expires_in_seconds) : undefined,
      message: data.message ? String(data.message) : undefined,
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Request failed', code: 'network_error' };
  }
}

export async function confirmDeleteAccount(code: string, verificationId?: string | null) {
  const token = currentToken;
  if (!token) {
    return { success: false, error: 'Not authenticated', code: 'unauthorized' };
  }

  try {
    const payload: Record<string, unknown> = { code };
    if (verificationId) {
      payload.verification_id = verificationId;
    }
    const response = await fetch(`${API_BASE_URL}/auth/delete-account/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      const refreshed = await refreshSession({ silent: true });
      if (refreshed && currentToken) {
        return confirmDeleteAccount(code, verificationId);
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Delete failed' })) as LoginErrorResponse;
      return {
        success: false,
        error: String(error.error || 'Delete failed'),
        code: String(error.code || ''),
        attemptsRemaining: Number.isFinite(error.attempts_remaining) ? Number(error.attempts_remaining) : undefined,
      };
    }

    clearAuthState({ clearSessionHint: true });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Delete failed', code: 'network_error' };
  }
}

export async function checkUsernameAvailability(username: string) {
  const token = currentToken;
  if (!token) {
    return { success: false, error: 'Not authenticated', code: 'unauthorized' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/profile/username/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ username }),
    });

    if (response.status === 401) {
      const refreshed = await refreshSession({ silent: true });
      if (refreshed && currentToken) {
        return checkUsernameAvailability(username);
      }
    }

    const data = await response.json().catch(() => ({} as Record<string, unknown>));
    if (!response.ok) {
      return {
        success: false,
        available: false,
        error: String(data.error || 'Unable to check username'),
        code: String(data.code || ''),
      };
    }

    return {
      success: true,
      available: Boolean(data.available),
      current: Boolean(data.current),
      username: String(data.username || username),
      message: data.message ? String(data.message) : undefined,
    };
  } catch (error: any) {
    return { success: false, available: false, error: error.message || 'Unable to check username', code: 'network_error' };
  }
}

export async function updateUsername(username: string) {
  const token = currentToken;
  if (!token) {
    return { success: false, error: 'Not authenticated', code: 'unauthorized' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/profile/username`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ username }),
    });

    if (response.status === 401) {
      const refreshed = await refreshSession({ silent: true });
      if (refreshed && currentToken) {
        return updateUsername(username);
      }
    }

    const data = await response.json().catch(() => ({} as Record<string, unknown>));
    if (!response.ok) {
      return {
        success: false,
        error: String(data.error || 'Unable to update username'),
        code: String(data.code || ''),
      };
    }

    if (data.user) {
      setCurrentUser(data.user as User);
    }
    return {
      success: true,
      user: data.user as User,
      message: data.message ? String(data.message) : 'Username updated successfully.',
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Unable to update username', code: 'network_error' };
  }
}

export async function updateFirstName(firstName: string) {
  const token = currentToken;
  if (!token) {
    return { success: false, error: 'Not authenticated', code: 'unauthorized' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/profile/first-name`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ firstName }),
    });

    if (response.status === 401) {
      const refreshed = await refreshSession({ silent: true });
      if (refreshed && currentToken) {
        return updateFirstName(firstName);
      }
    }

    const data = await response.json().catch(() => ({} as Record<string, unknown>));
    if (!response.ok) {
      return {
        success: false,
        error: String(data.error || 'Unable to update first name'),
        code: String(data.code || ''),
      };
    }

    if (data.user) {
      setCurrentUser(data.user as User);
    }
    return {
      success: true,
      user: data.user as User,
      message: data.message ? String(data.message) : 'First name updated successfully.',
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Unable to update first name', code: 'network_error' };
  }
}

export async function updatePassword(currentPassword: string, newPassword: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/profile/password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const refreshed = await refreshSession({ silent: true });
        if (refreshed) {
          return updatePassword(currentPassword, newPassword);
        }
      }
      const error = await response.json().catch(() => ({ error: 'Password update failed' }));
      return {
        success: false,
        error: String(error.error || 'Password update failed'),
        code: String(error.code || ''),
      };
    }

    const data = await response.json();
    return {
      success: true,
      message: String(data.message || 'Password updated successfully.'),
    };
  } catch {
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export async function verifyCurrentPassword(currentPassword: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/profile/password/verify-current`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ currentPassword }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const refreshed = await refreshSession({ silent: true });
        if (refreshed) {
          return verifyCurrentPassword(currentPassword);
        }
      }
      const error = await response.json().catch(() => ({ error: 'Password verification failed' }));
      return {
        success: false,
        error: String(error.error || 'Password verification failed'),
        code: String(error.code || ''),
      };
    }

    const data = await response.json();
    return {
      success: true,
      message: String(data.message || 'Password confirmed.'),
    };
  } catch {
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export async function requestEmailChangeCode(email: string) {
  const token = currentToken;
  if (!token) {
    return { success: false, error: 'Not authenticated', code: 'unauthorized' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/profile/email/request-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    if (response.status === 401) {
      const refreshed = await refreshSession({ silent: true });
      if (refreshed && currentToken) {
        return requestEmailChangeCode(email);
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' })) as LoginErrorResponse;
      return {
        success: false,
        error: String(error.error || 'Request failed'),
        code: String(error.code || ''),
      };
    }

    const data = await response.json() as VerificationPendingResponse;
    return {
      success: true,
      verificationId: data.verification_id ? String(data.verification_id) : undefined,
      destination: data.destination ? String(data.destination) : undefined,
      expiresAt: Number.isFinite(data.expires_at) ? Number(data.expires_at) : undefined,
      expiresInSeconds: Number.isFinite(data.expires_in_seconds) ? Number(data.expires_in_seconds) : undefined,
      message: data.message ? String(data.message) : undefined,
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Request failed', code: 'network_error' };
  }
}

export async function confirmEmailChange(code: string, verificationId?: string | null) {
  const token = currentToken;
  if (!token) {
    return { success: false, error: 'Not authenticated', code: 'unauthorized' };
  }

  try {
    const payload: Record<string, unknown> = { code };
    if (verificationId) {
      payload.verification_id = verificationId;
    }

    const response = await fetch(`${API_BASE_URL}/auth/profile/email/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      const refreshed = await refreshSession({ silent: true });
      if (refreshed && currentToken) {
        return confirmEmailChange(code, verificationId);
      }
    }

    const data = await response.json().catch(() => ({} as Record<string, unknown>));
    if (!response.ok) {
      return {
        success: false,
        error: String(data.error || 'Unable to update email'),
        code: String(data.code || ''),
        attemptsRemaining: Number.isFinite(data.attempts_remaining) ? Number(data.attempts_remaining) : undefined,
      };
    }

    if (data.user) {
      setCurrentUser(data.user as User);
    }
    return {
      success: true,
      user: data.user as User,
      message: data.message ? String(data.message) : 'Email updated successfully.',
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Unable to update email', code: 'network_error' };
  }
}

export async function logout(options: { redirectToLogin?: boolean; reason?: string } = {}) {
  return logoutWithOptions(options);
}

async function logoutWithOptions(options: { redirectToLogin?: boolean; reason?: string }) {
  const token = currentToken;
  clearAuthState({ clearSessionHint: true });
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: token
        ? { Authorization: `Bearer ${token}` }
        : undefined,
    });
  } catch {
    // best-effort logout
  }
  if (options.redirectToLogin) {
    redirectToLogin(options.reason || 'session_expired');
  }
}

export async function logoutAndRedirect(reason = 'session_expired') {
  await logoutWithOptions({ redirectToLogin: true, reason });
}

export function getUser(): User | null {
  return currentUser;
}

export function getToken(): string | null {
  return currentToken;
}

export function isAuthenticated(): boolean {
  return !!currentToken && !!currentUser;
}

export async function updateProfile(firstName: string) {
  return updateFirstName(firstName);
}
